import { Injectable } from '@angular/core';
import { forkJoin, Observable } from 'rxjs';
import { toArray, mergeMap, take, tap, map, filter } from 'rxjs';
import { select, Store } from '@ngrx/store';
import { QueryBuilderConfig } from 'ngx-angular-query-builder';
import { SelectItem } from 'primeng/api';

import { ApplicationState } from '@store/application-store';
import * as arrearsActions from '../actions/arrears.actions';
import { FormDefinition, FormFieldDefinition } from '@feature/_app_modules/dynamic-forms/dynamic-form/models/dynamic-form.interfaces';
import { WidgetConfiguration, WidgetConfigurationsService } from '@feature/widget-configs';
import { WidgetAdminHandler , isWidgetType } from '@widget-admin/services/widget-admin-handler';
import { getDefaultWidgetConfigAdminForm, addFilterFieldsToAdminForm, addColumnFieldsToAdminForm } from '@widget-admin/services/widget-admin-form';
import { Currency, CurrencyService } from '@currency/old_index';
import { mapWherePropertiesMatch } from '@common/index';
import { ChartGroupsProvider } from '../services/chart-groups-provider';
import { DebtAnalysisAdminWidgetConfiguration } from '../models/debt-analysis-admin-widget-configuration.model';
import { DebtAnalysisWidgetConfiguration } from '../models/debt-analysis-widget-configuration.model';
import { DebtAnalysisWidgetConfigurationHttpService } from '../services/debt-analysis-widget-configuration.http.service';
import { ArrearsTransactionTableColumnsHttpService } from '../services/arrears-transaction-table-columns-http.service';
import { IColumnDefinition } from '@common/models/column-definition';
import * as arrearsSelectors from '../selectors/arrears.selectors';
import { ArrearsViewTypes } from '../models/arrears-view-types';

@Injectable({
  providedIn: 'root'
})
export class ArrearsWidgetAdminHandler implements WidgetAdminHandler {

  private defaultCurrencyCode: string;
  private currencyOptions: SelectItem[];
  private queryBuilderConfig: QueryBuilderConfig;
  private configs: DebtAnalysisWidgetConfiguration[];
  private columns: IColumnDefinition[];

  constructor(
    private readonly store: Store<ApplicationState>,
    private readonly widgetConfigService: WidgetConfigurationsService,
    private readonly currencyService: CurrencyService,
    private readonly debtAnalysisWidgetConfigurationHttpService: DebtAnalysisWidgetConfigurationHttpService,
    private readonly arrearsTransactionTableColumnsHttpService: ArrearsTransactionTableColumnsHttpService,
    private readonly chartGroupsProvider: ChartGroupsProvider) {
  }

  private getDebtAnalysisConfiguration(widgetConfigurationId: number): DebtAnalysisWidgetConfiguration {
    const match = this.configs.find(x => x.WidgetConfigurationId === widgetConfigurationId);

    const defaultItem = () => {
      const item = new DebtAnalysisWidgetConfiguration();
      item.GroupByField1 = 'Age';
      item.GroupByField2 = 'Property';
      item.View = 'pie';
      item.Currency = 'GBP';
      return item;
    };

    return match ? match : defaultItem();

  }

  getAdminForm(selectedConfig: WidgetConfiguration): FormDefinition {
    let formDefinition = getDefaultWidgetConfigAdminForm(selectedConfig);
    formDefinition = addFilterFieldsToAdminForm(selectedConfig, this.queryBuilderConfig, formDefinition);
    formDefinition = addColumnFieldsToAdminForm(selectedConfig, this.columns, formDefinition, 'Transaction Columns');

    const debtAnalysisConfig = this.getDebtAnalysisConfiguration(selectedConfig.id);

    if (!isWidgetType('ArrearsTable', selectedConfig) && !isWidgetType('ArrearsChart', selectedConfig)) {
      return formDefinition;
    }

    formDefinition.fields.push({
      key: 'defaultChartView',
      dataKey: 'value',
      optionLabel: 'label',
      label: 'Default Chart View',
      value: debtAnalysisConfig ? { value: debtAnalysisConfig.View } : { value: ArrearsViewTypes.agedArrearsTable },
      type: 'dropdown',
      disabled: false,
      options: [
        { label: 'Table', value: ArrearsViewTypes.agedArrearsTable },
        { label: 'Bar Chart', value: ArrearsViewTypes.bar },
        { label: 'Pie Chart', value: ArrearsViewTypes.pie },
        { label: 'Doughnut Chart', value: ArrearsViewTypes.doughnut }
      ],
      validation: [],
      asyncValidation: [],
    } as FormFieldDefinition);

    formDefinition.fields.push({
      key: 'defaultDebtByGroup1',
      dataKey: 'Field',
      optionLabel: 'Name',
      label: 'Default Debt By (1)',
      value: debtAnalysisConfig ? { Field: debtAnalysisConfig.GroupByField1 } : { Field: this.chartGroupsProvider.defaultFirstGroup.Field },
      type: 'dropdown',
      disabled: false,
      options: this.chartGroupsProvider.getGroups(),
      validation: [],
      asyncValidation: [],
    } as FormFieldDefinition);

    formDefinition.fields.push({
      key: 'defaultDebtByGroup2',
      dataKey: 'Field',
      optionLabel: 'Name',
      label: 'Default Debt By (2)',
      value: debtAnalysisConfig ? { Field: debtAnalysisConfig.GroupByField2 } : { Field: this.chartGroupsProvider.defaultSecondGroup.Field },
      type: 'dropdown',
      disabled: false,
      options: this.chartGroupsProvider.getGroups(),
      validation: [],
      asyncValidation: [],
    } as FormFieldDefinition);

    formDefinition.fields.push({
      key: 'currency',
      dataKey: 'value',
      optionLabel: 'label',
      label: 'Currency Code',
      value: debtAnalysisConfig ?
        { value: debtAnalysisConfig.Currency } :
        { value: this.defaultCurrencyCode },
      type: 'dropdown',
      disabled: false,
      options: this.currencyOptions,
      validation: [],
      asyncValidation: [],
    } as FormFieldDefinition);

    return formDefinition;
  }

  private createSaveRequest(selectedConfig: WidgetConfiguration, adminFormValue: any): DebtAnalysisAdminWidgetConfiguration {

    const copyOfSelectedConfig: WidgetConfiguration = mapWherePropertiesMatch(selectedConfig, adminFormValue);

    const adminConfig = new DebtAnalysisAdminWidgetConfiguration();
    const config = new DebtAnalysisWidgetConfiguration();
    config.View = adminFormValue.defaultChartView.value;
    config.WidgetConfigurationId = selectedConfig.id;
    config.GroupByField1 = adminFormValue.defaultDebtByGroup1.Field;
    config.GroupByField2 = adminFormValue.defaultDebtByGroup2.Field;
    config.Currency = adminFormValue.currency.value;
    adminConfig.DebtAnalysisWidgetConfiguration = config;
    adminConfig.WidgetConfiguration = copyOfSelectedConfig;
    return adminConfig;
  }

  saveConfigFromAdminForm(selectedConfig: WidgetConfiguration, adminFormValue: any): Observable<boolean> {

    const saveRequest = this.createSaveRequest(selectedConfig, adminFormValue);

    return this.debtAnalysisWidgetConfigurationHttpService.saveAdminConfiguration(saveRequest).pipe(map(s => true));
  }

  createCopyFromAdminForm(selectedConfig: WidgetConfiguration, adminFormValue: any): Observable<boolean> {

    const saveRequest = this.createSaveRequest(selectedConfig, adminFormValue);

    return this.debtAnalysisWidgetConfigurationHttpService.createCopyOfConfiguration(saveRequest).pipe(map(s => true));

  }

  deleteConfigFromAdminForm(selectedConfig: WidgetConfiguration, adminFormValue: any): Observable<boolean> {
    const copyOfSelectedConfig: WidgetConfiguration = mapWherePropertiesMatch(selectedConfig, adminFormValue);
    return this.widgetConfigService.delete(copyOfSelectedConfig).pipe(map(() => true));
  }


  initialise(): Observable<boolean> {


    this.store.dispatch(new arrearsActions.LoadAdminData());

    const getDebtAnalysisConfigs = this.debtAnalysisWidgetConfigurationHttpService.getConfigs().pipe(
      take(1),
      tap(configs => this.configs = configs),
      map(() => true));


    const getAllColumns = this.arrearsTransactionTableColumnsHttpService.getAllTableColumns().pipe(
      take(1),
      tap(columns => this.columns = columns)
      );

    const getDefaultCurrency = this.currencyService.getDefaultCurrency().pipe(
      take(1),
      tap(currency => this.defaultCurrencyCode = currency.Code),
    );

    const getAllCurrencies = this.currencyService.getCurrencies().pipe(
      take(1),
      mergeMap((items: Currency[]) => items),
      map((c: Currency) => {
        return {
          value: c.Code,
          label: c.Code
        } as SelectItem;
      }),
      toArray(),
      tap(currencyOptions => this.currencyOptions = currencyOptions)
    );

    const getDebtAnalysisFilterConfig = this.store.pipe(
      select(arrearsSelectors.getAdminFilterConfig()),
      filter(queryBuilderConfig => queryBuilderConfig != undefined),
      tap(queryBuilderConfig => this.queryBuilderConfig = queryBuilderConfig ),
      take(1)
    );

    const all = forkJoin([
        getDebtAnalysisConfigs,
        getDefaultCurrency,
        getAllColumns,
        getAllCurrencies,
        getDebtAnalysisFilterConfig]
    ).pipe(map(() => true));

    return all;

  }

}
